/**
 * Attaches the collector's `x-write-key` header to proxied Mixpanel track requests — and to nothing else.
 *
 * Why not the SDK's own `xhr_headers` config: it is global, so the header would ride every request the SDK
 * makes, including the profile (`engage`) and Session Replay (`record`) calls that still go straight to
 * Mixpanel. Verified against Mixpanel's live preflights (in `predictions-ui`, where this module comes from):
 * `/engage/` returns no `Access-Control-Allow-Headers` at all, and `/record/` allows only
 * `X-Requested-With, Content-Type`. A browser therefore BLOCKS both requests outright once an unlisted custom
 * header is added — silently killing user profiles and replay uploads.
 *
 * So the header is scoped by origin instead: a narrow XMLHttpRequest patch that adds it only to requests aimed
 * at the proxy. Anything else — Mixpanel direct, player API calls, third-party scripts — passes through
 * untouched.
 *
 * Live wherever `VITE_MIXPANEL_TRACK_API_HOST` names a collector (`t.dev.crowncoinscasino.com` in the dev modes,
 * `t.crowncoinscasino.com` in production); inert in tests and any build that leaves it blank.
 */

/** Header the collector authenticates with (mirrors its CORS allow-list). */
export const PROXY_WRITE_KEY_HEADER = 'x-write-key';

/** Where `open()` stashes the target URL for `send()` to read back. */
const URL_KEY = '__mpProxyTargetUrl';

interface TaggedXhr extends XMLHttpRequest {
  [URL_KEY]?: string;
}

let installed = false;

/**
 * Install the origin-scoped header injection. Idempotent and inert unless both a proxy host and a write key
 * are configured. A malformed host is ignored rather than guessed at — mis-targeting would leak the key to a
 * third party.
 *
 * @returns Whether this call installed the patch. `false` on a repeat call, a blank argument, a malformed
 *   host, or an environment with no `XMLHttpRequest`.
 */
export function installMixpanelProxyAuth(proxyHost: string, writeKey: string): boolean {
  if (installed || !proxyHost || !writeKey) return false;
  if (typeof XMLHttpRequest === 'undefined' || typeof location === 'undefined') return false;

  let proxyOrigin: string;
  try {
    proxyOrigin = new URL(proxyHost).origin;
  } catch {
    return false;
  }

  const proto = XMLHttpRequest.prototype;
  // Deliberately unbound: each is re-applied to the live request (`.apply(this, …)`) inside its replacement.
  /* eslint-disable @typescript-eslint/unbound-method */
  const originalOpen = proto.open;
  const originalSend = proto.send;
  /* eslint-enable @typescript-eslint/unbound-method */

  // `open`/`send` are overloaded, so the replacements take a loose arg list and are cast back.
  type AnyFn = (...args: unknown[]) => void;

  proto.open = function openWithProxyTag(this: TaggedXhr, method: string, url: string | URL, ...rest: unknown[]) {
    try {
      this[URL_KEY] = String(url);
    } catch {
      // Tagging is best-effort; never break the request.
    }
    return (originalOpen as unknown as AnyFn).apply(this, [method, url, ...rest]);
  } as typeof proto.open;

  proto.send = function sendWithProxyAuth(this: TaggedXhr, ...args: unknown[]) {
    try {
      const target = this[URL_KEY];
      // setRequestHeader is only legal between open() and send() — here.
      if (target && new URL(target, location.href).origin === proxyOrigin) {
        this.setRequestHeader(PROXY_WRITE_KEY_HEADER, writeKey);
      }
    } catch {
      // A failed header add must not stop the event from being attempted.
    }
    return (originalSend as unknown as AnyFn).apply(this, args);
  } as typeof proto.send;

  installed = true;
  return true;
}

/** Test-only escape hatch. Internal to the analytics surface. */
export function __resetProxyAuthForTests(): void {
  installed = false;
}
